import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { SimpleSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { CustomHttpErrorResponse, Error, ListServerPaginationStateModel } from '../../../../../../common';
import { PartnerBrief, PartnerExtIdsModel, PartnerExtIdType } from '../../../../administration';
import { NavigateUserService } from '../../../../../services/navigate-user.service';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import {
  FailedMessagesPartnerModel,
  FailedMessagesProfileItemFilterModel,
  FailedMessagesProfileItemModel,
} from '../../models/profile/failed-messages-profile-item.model';
import { FailedMessagesProfileItemBriefService } from './failed-messages-profile-item-brief.service';
import {
  FailedMessagesProfileItemBriefDelete,
  FailedMessagesProfileItemBriefInitialState,
  FailedMessagesProfileItemBriefLoad,
  FailedMessagesProfileItemBriefPartnerListLoad,
  FailedMessagesProfileItemBriefUpdate,
} from './failed-messages-profile-item-brief.actions';

export interface FailedMessagesProfileItemBriefStateModel {
  profile: FailedMessagesProfileItemModel;
  partners: FailedMessagesPartnerModel[];
  loaded: boolean;
}

const initialState: FailedMessagesProfileItemBriefStateModel = {
  profile: new FailedMessagesProfileItemModel(),
  partners: [],
  loaded: false,
};

@State<FailedMessagesProfileItemBriefStateModel>({
  name: 'failedMessagesProfileItemBrief',
  defaults: initialState,
})
@Injectable()
export class FailedMessagesProfileItemBriefState {
  constructor(
    private failedMessagesProfileItemBriefService: FailedMessagesProfileItemBriefService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getProfileId(state: FailedMessagesProfileItemBriefStateModel): number {
    return state.profile.id;
  }

  @Selector()
  static getState(state: FailedMessagesProfileItemBriefStateModel): FailedMessagesProfileItemBriefStateModel {
    return state;
  }

  @Selector()
  static getProfile(state: FailedMessagesProfileItemBriefStateModel): FailedMessagesProfileItemModel {
    return state.profile;
  }

  @Selector()
  static getPartners(state: FailedMessagesProfileItemBriefStateModel): FailedMessagesPartnerModel[] {
    return state.partners;
  }

  @Selector()
  static getLoaded(state: FailedMessagesProfileItemBriefStateModel): boolean {
    return state.loaded;
  }

  @Action(FailedMessagesProfileItemBriefLoad)
  getProfileItemBrief(
    ctx: StateContext<FailedMessagesProfileItemBriefStateModel>,
    { payload }: FailedMessagesProfileItemBriefLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.failedMessagesProfileItemBriefService.getProfile(payload).pipe(
      map((profile: FailedMessagesProfileItemFilterModel) => {
        const { filter: _filter, ...briefProfile } = profile;
        ctx.patchState({ profile: briefProfile, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(FailedMessagesProfileItemBriefPartnerListLoad)
  getPartnerList(
    ctx: StateContext<FailedMessagesProfileItemBriefStateModel>,
    { payload }: FailedMessagesProfileItemBriefPartnerListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.failedMessagesProfileItemBriefService.getPartnerList(payload).pipe(
      map((partnerList: ListServerPaginationStateModel<PartnerBrief>) => {
        const partners = partnerList.items.map((item: PartnerBrief) => {
          const extId = item.extIds.find(
            (extIdItem: PartnerExtIdsModel) => extIdItem.type === PartnerExtIdType.Tms,
          )?.id;
          return new FailedMessagesPartnerModel({ id: item.id, name: item.name, extId: extId ?? null });
        });
        ctx.patchState({ partners, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(FailedMessagesProfileItemBriefUpdate)
  updateProfileItemBrief(
    ctx: StateContext<FailedMessagesProfileItemBriefStateModel>,
    { payload }: FailedMessagesProfileItemBriefUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.failedMessagesProfileItemBriefService.updateProfile(payload).pipe(
      map((updatedProfile: FailedMessagesProfileItemFilterModel) => {
        ctx.patchState({ profile: updatedProfile, loaded: true });
        this.notificationService.success('api.failedMessagesProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(FailedMessagesProfileItemBriefDelete)
  deleteProfileItemBrief(
    ctx: StateContext<FailedMessagesProfileItemBriefStateModel>,
    { payload }: FailedMessagesProfileItemBriefDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.failedMessagesProfileItemBriefService.deleteProfile(payload).pipe(
      map(() => {
        this.notificationService.success('api.failedMessagesProfile.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/data-quality/failed-messages-profiles');
        return ctx.dispatch(new FailedMessagesProfileItemBriefInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(FailedMessagesProfileItemBriefInitialState)
  setInitialState(ctx: StateContext<FailedMessagesProfileItemBriefStateModel>): void {
    ctx.setState(initialState);
  }
}
